/**
 * LiveTrendPreview — recharts line + a live "ghost" point at currentValue.
 * Redraws as the user adjusts the slider. No new chart library.
 */
import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { getIdealRangeForParameter } from '@reefapp/shared';
import type { TestLogEntry, ParameterKey } from '@reefapp/shared';

interface Point { date: string; value?: number; ghost?: number }

export interface LiveTrendPreviewProps {
  logs: ReadonlyArray<TestLogEntry>;
  parameterKey: ParameterKey;
  currentValue: number;
  className?: string;
}

export function LiveTrendPreview({
  logs,
  parameterKey,
  currentValue,
  className,
}: LiveTrendPreviewProps) {
  const data: Point[] = useMemo(() => {
    const hist = logs
      .filter((l) => l.parameterKey === parameterKey && typeof l.result === 'number')
      .map((l) => ({ date: new Date(l.date).toLocaleDateString(), value: l.result as number }))
      .reverse();
    return [...hist, { date: new Date().toLocaleDateString(), ghost: currentValue }];
  }, [logs, parameterKey, currentValue]);

  const idealRange = getIdealRangeForParameter(parameterKey);

  if (data.length < 2) {
    return (
      <p className="text-text-3 text-sm text-center py-4">
        Log at least one reading to see the trend.
      </p>
    );
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 10 }} />
          <YAxis domain={['auto', 'auto']} stroke="#64748b" tick={{ fontSize: 10 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }}
          />
          <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={2} dot={false} connectNulls name={parameterKey} />
          <Line type="monotone" dataKey="ghost" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 5, fill: '#f59e0b' }} name="current" />
          {idealRange && (
            <>
              <ReferenceLine y={idealRange.min} stroke="#facc15" strokeDasharray="5 5" />
              <ReferenceLine y={idealRange.max} stroke="#facc15" strokeDasharray="5 5" />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
